import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { supabase } from '../supabaseClient';
import { setViewerEntitlement } from '../utils/trackAccess';
import { useAuth } from './AuthContext';
import { userRow, invalidateUserRow } from '../utils/userRow';

const TierContext = createContext(null);

export function TierProvider({ children }) {
  const tier = useTierInternal();
  return <TierContext.Provider value={tier}>{children}</TierContext.Provider>;
}

// Feature access map per tier
const TIER_ACCESS = {
  free: {
    max_singles: 2,
    max_albums: 0,
    lyrics: false,
    custom_theme: false,
    // WAS true, WHILE EVERYTHING ELSE SAID OTHERWISE.
    //
    // platform_tiers.can_create_chat_rooms is false for free, the Free card in
    // src/data/tiers.js lists chat rooms as not included, and handleCreate in
    // ChatRoomsPage refuses a free artist outright. Only this line said yes,
    // and this line is the one TierGate reads. So a free artist was shown the
    // whole create form, named their room, picked a colour, pressed create,
    // and was told then.
    chat_rooms: false,
    analytics: false,
    collaborations: false,
    priority_trending: false,
    download_sales: false,
    pre_order: false,
    custom_branding: false,
    advanced_analytics: false,
    offline_listening: false,            // Free: streaming only
    // ── THE CREATE MENU ──────────────────────────────────────────────────
    //
    // Stories, voice memos, live sessions and the follower broadcast were in
    // no tier map at all, so hasFeature() answered false for every one of
    // them and nothing ever asked. They were reachable by every artist on
    // every plan, and the only reason that never looked like a leak is that
    // nobody had used them: zero stories, zero memos, zero live sessions and
    // zero chat rooms across all 77 free artists on the day this was written.
    //
    // live_streaming sits at premium rather than pro because that is what the
    // cards say: the Premium card sells "Live streaming to followers" and the
    // Pro card lists it as not included.
    stories: false,
    voice_memo: false,
    fan_broadcast: false,
    live_streaming: false,
    // Sold on the Premium card as "YouTube video backdrop", and listed as not
    // included on Pro. The field was wrapped in a download_sales gate, which
    // is a Pro feature, so every Pro artist had it.
    video_backdrop: false,
    // "Tip goals & fan fundraising" on the Premium card. TipGoal.js had no
    // tier reference of any kind, so a free artist could set one from their
    // own profile. Gating creating and editing only: an existing goal keeps
    // showing and keeps taking tips whatever the plan says, because taking a
    // public fundraiser off somebody's page is not a thing to do by migration.
    tip_goals: false,
    // Beatmaker limits
    max_beats: 3,                        // Free: 3 beats total
    beat_licences: ['free', 'basic'],    // Free: only Free and Basic Lease
    stems_upload: false,                 // Free: no stem uploads
    beat_analytics: false,               // Free: no beat analytics
    exclusive_licence: false,            // Free: no Exclusive licence tier
  },
  pro: {
    max_singles: Infinity,
    max_albums: Infinity,
    lyrics: true,
    custom_theme: true,
    chat_rooms: true,
    analytics: true,
    collaborations: true,
    priority_trending: false,
    download_sales: true,
    download_sales_monthly_limit: 2,
    pre_order: false,
    custom_branding: true,
    advanced_analytics: false,
    community_post: true,
    daily_thought: true,
    offline_listening: true,             // Pro: carry music with no signal
    // The create menu. Live streaming stays off: it is a Premium line, and so
    // are the video backdrop and tip goals.
    stories: true,
    voice_memo: true,
    fan_broadcast: true,
    live_streaming: false,
    video_backdrop: false,
    tip_goals: false,
    // Beatmaker limits
    max_beats: 20,                                            // Pro: 20 beats
    beat_licences: ['free', 'basic', 'premium', 'unlimited'],  // Pro: all except Exclusive
    stems_upload: true,
    beat_analytics: true,
    exclusive_licence: false,                                   // Pro: no Exclusive
  },

  premium: {
    max_singles: Infinity,
    max_albums: Infinity,
    lyrics: true,
    custom_theme: true,
    chat_rooms: true,
    analytics: true,
    collaborations: true,
    priority_trending: true,
    download_sales: true,
    download_sales_monthly_limit: Infinity,
    pre_order: true,
    custom_branding: true,
    advanced_analytics: true,
    community_post: true,
    daily_thought: true,
    offline_listening: true,             // Premium: carry music with no signal
    // The create menu, in full.
    stories: true,
    voice_memo: true,
    fan_broadcast: true,
    live_streaming: true,
    video_backdrop: true,
    tip_goals: true,
    // Beatmaker limits
    max_beats: Infinity,                                                    // Premium: unlimited
    beat_licences: ['free', 'basic', 'premium', 'unlimited', 'exclusive'],   // Premium: all tiers
    stems_upload: true,
    beat_analytics: true,
    exclusive_licence: true,
  },
};

// ── Listener tier feature map ─────────────────────────────────────────────────
export const LISTENER_TIER_ACCESS = {
  free: {
    app_themes:             false,
    deep_stats:             false,
    fan_badge:              false,
    free_downloads_monthly: 0,
    early_access:           false,
    offline_listening:      false,
    offline_track_limit:    0,
  },
  pro: {
    app_themes:             true,
    deep_stats:             true,
    fan_badge:              true,
    free_downloads_monthly: 3,
    early_access:           true,
    // Keeping tracks on the device to play with no connection. Deliberately
    // separate from free_downloads_monthly: a download is a file you keep and
    // it costs the artist a download count, offline is listening with the
    // network removed and costs nothing. Conflating them would stop a Fan Pro
    // listener after three songs.
    offline_listening:      true,
    offline_track_limit:    500,
  },
};

export function getListenerFeature(tier, feature) {
  const map = LISTENER_TIER_ACCESS[tier] || LISTENER_TIER_ACCESS.free;
  return map[feature] ?? false;
}

// Human-readable feature names for upgrade prompts
const FEATURE_LABELS = {
  lyrics: { name: 'Lyrics', description: 'Add lyrics to your tracks', minTier: 'pro' },
  custom_theme: { name: 'Custom Theme', description: 'Customize your artist profile page', minTier: 'pro' },
  chat_rooms: { name: 'Chat Rooms', description: 'Create chat rooms for your fans', minTier: 'pro' },
  analytics: { name: 'Analytics', description: 'View detailed track and audience analytics', minTier: 'pro' },
  collaborations: { name: 'Collaborations', description: 'Collaborate with other artists and set royalty splits', minTier: 'pro' },
  priority_trending: { name: 'Priority Trending', description: 'Get boosted visibility in browse and trending', minTier: 'premium' },
  download_sales: { name: 'Download Sales', description: 'Sell track downloads directly to fans (Pro: 2/month, Premium: unlimited)', minTier: 'pro' },
  custom_branding: { name: 'Custom Branding', description: 'Full branding control on your profile', minTier: 'pro' },
  advanced_analytics: { name: 'Advanced Analytics', description: 'Deep audience insights and export tools', minTier: 'premium' },
  community_post: { name: 'Community Posts', description: 'Share updates and music with your fans', minTier: 'pro' },
  unlimited_uploads: { name: 'Unlimited Uploads', description: 'Upload unlimited tracks and albums', minTier: 'pro' },
  daily_thought: { name: 'Daily Thought', description: 'Post a daily message on your artist profile', minTier: 'pro' },
  offline_listening: { name: 'Offline Listening', description: 'Save tracks to your device and play them with no connection', minTier: 'pro' },
  pre_order: { name: 'Pre-order Releases', description: 'Let fans pre-save upcoming releases before they drop', minTier: 'premium' },
  stems_upload: { name: 'Stem Uploads', description: 'Attach stems to your beats for buyers to download', minTier: 'pro' },
  beat_analytics: { name: 'Beat Analytics', description: 'Per-beat plays, licence views and purchase tracking', minTier: 'pro' },
  exclusive_licence: { name: 'Exclusive Licence', description: 'Offer full exclusive rights on your beats', minTier: 'premium' },
  stories: { name: 'Stories', description: 'Share a 24 hour clip with your followers', minTier: 'pro' },
  voice_memo: { name: 'Voice Memos', description: 'Record a short message for your fans', minTier: 'pro' },
  fan_broadcast: { name: 'Message Fans', description: 'Send a notification to everyone who follows you', minTier: 'pro' },
  live_streaming: { name: 'Live Streaming', description: 'Go live to your followers, audio or video', minTier: 'premium' },
  video_backdrop: { name: 'Video Backdrop', description: 'Play a YouTube video behind a track in the player', minTier: 'premium' },
  tip_goals: { name: 'Tip Goals', description: 'Set a fundraising goal on your profile and show the progress', minTier: 'premium' },
}

function useTierInternal() {
  const { artist, user, isAdmin } = useAuth();
  const [tierSlug, setTierSlug] = useState('free');
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackCount, setTrackCount] = useState(0);
  const [monthlyDownloadSalesCount, setMonthlyDownloadSalesCount] = useState(0);
  const [listenerTierSlug, setListenerTierSlug] = useState('free');

  useEffect(() => {
    if (isAdmin) {
      setTierSlug('premium');
      setLoading(false);
      return;
    }
    if (artist) {
      fetchTier(artist.id);
      fetchTrackCount(artist.id);
      fetchMonthlyDownloadSalesCount(artist.id);
      // Also fetch listener tier — artist accounts can have fan_pro too
      if (user?.id) fetchListenerTier(user.id);
    } else if (user?.id) {
      fetchListenerTier(user.id);
    } else {
      setTierSlug('free');
      setListenerTierSlug('free');
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artist?.id, artist?.role, user?.id, isAdmin]);

  const fetchListenerTier = async (userId) => {
    try {
      // Primary: check listeners.tier + tier_expires_at (set by webhook)
      // Shares the same read as AuthContext and the theme init rather than
      // sending a third one. See src/utils/userRow.js.
      const listenerRow = await userRow('listeners', userId);

      // An explicit 'free' is an ANSWER, not a miss.
      //
      // This used to fall straight through to the subscriptions table whenever
      // listeners.tier was 'free', which meant an admin toggling somebody down
      // to free did nothing at all: the old active grant row was still there,
      // the fallback found it, and the account resolved back to pro. "Set to
      // free" and "we have no idea what this account is" were the same state
      // to this function. They are not the same thing, and only the second one
      // should be asking the subscriptions table.
      if (listenerRow && listenerRow.tier === 'free') {
        setListenerTierSlug('free');
        setLoading(false);
        return;
      }

      if (listenerRow?.tier && listenerRow.tier !== 'free') {
        // Check expiry
        if (listenerRow.tier_expires_at) {
          const isExpired = new Date(listenerRow.tier_expires_at) < new Date();
          if (isExpired) {
            // Auto-downgrade expired tier
            await supabase.from('listeners')
              .update({ tier: 'free', updated_at: new Date().toISOString() })
              .eq('user_id', userId);
            // This row is shared now, so a write to it has to drop the cached
            // copy or the rest of the app keeps reading the old tier.
            invalidateUserRow('listeners', userId);
            setListenerTierSlug('free');
            setLoading(false);
            return;
          }
        }
        // Map listeners.tier → canonical slug
        const tierMap = { fan_pro: 'pro', pro: 'pro', premium: 'premium', free: 'free' };
        setListenerTierSlug(tierMap[listenerRow.tier] || 'free');
        setLoading(false);
        return;
      }

      // Fallback: check listener_tier_subscriptions table
      const { data: sub } = await supabase
        .from('listener_tier_subscriptions')
        .select('tier_id, status, expires_at')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (sub?.tier_id) {
        // Check expiry
        if (sub.expires_at && new Date(sub.expires_at) < new Date()) {
          await supabase.from('listener_tier_subscriptions')
            .update({ status: 'expired', updated_at: new Date().toISOString() })
            .eq('user_id', userId)
            .eq('tier_id', sub.tier_id);
          setListenerTierSlug('free');
          setLoading(false);
          return;
        }
        // Fan Pro has its own platform_tiers row now (migration 111). The
        // OLD artist-pro id stays in this map deliberately: listener
        // subscriptions created before migration 112 repointed them still
        // carry it, and dropping it here would resolve those listeners to
        // 'free' — silently taking away offline listening and their three
        // monthly downloads. Both ids mean the same entitlement, so both map
        // to 'pro'. The artist-pro entry can be removed once no listener
        // subscription references it.
        const TIER_ID_MAP = {
          '289f65ec-4b2f-4868-b71f-9f560d493225': 'free',
          '917a2c63-815d-48db-80d0-5d9d3078ee9b': 'pro',      // fan_pro (current)
          'a421dac1-f492-461c-88a5-f01b6942a042': 'pro',      // artist pro (legacy listener rows)
          'f0b8b8f5-bfc2-496e-9fb4-8904d9dc6fe4': 'premium',
        };
        setListenerTierSlug(TIER_ID_MAP[sub.tier_id] || 'free');
      } else {
        setListenerTierSlug('free');
      }
    } catch { setListenerTierSlug('free'); }
    setLoading(false);
  };

  // Re-check tier when user returns from Safari (iOS PayPal hop)
  useEffect(() => {
    if (!artist?.id || isAdmin) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchTier(artist.id);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [artist?.id, artist?.role, isAdmin]);

  const fetchTier = async (artistId) => {
    if (!artistId) return;
    try {
      // Fetch active subscription then resolve tier slug separately
      // (two FK constraints to platform_tiers prevent reliable PostgREST join)
      const { data: subs } = await supabase
        .from('artist_tier_subscriptions')
        .select('tier_id, status')
        .eq('artist_id', artistId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      const sub = subs?.[0];

      if (sub?.tier_id) {
        // Hardcoded tier_id → slug mapping to avoid platform_tiers RLS issues
        const TIER_ID_MAP = {
          '289f65ec-4b2f-4868-b71f-9f560d493225': 'free',
          'a421dac1-f492-461c-88a5-f01b6942a042': 'pro',
          'f0b8b8f5-bfc2-496e-9fb4-8904d9dc6fe4': 'premium',
        };
        const slug = TIER_ID_MAP[sub.tier_id];
        if (slug) {
          setTierSlug(['master','premium'].includes(slug) ? 'premium' : slug === 'pro' ? 'pro' : 'free');
          setLoading(false);
          return;
        }
        // Fallback: try platform_tiers query
        try {
          const { data: tierRow } = await supabase
            .from('platform_tiers')
            .select('id, slug')
            .eq('id', sub.tier_id)
            .single();
          if (tierRow?.slug) {
            setTierSlug(['master','premium'].includes(tierRow.slug) ? 'premium' : tierRow.slug === 'pro' ? 'pro' : 'free');
            setLoading(false);
            return;
          }
        } catch {}
      }

      // Fallback: check artists.tier column
      const { data: artistRow } = await supabase
        .from('artists')
        .select('tier')
        .eq('id', artistId)
        .maybeSingle();
      const fallback = artistRow?.tier || 'free';
      setTierSlug(['master','premium'].includes(fallback) ? 'premium' : fallback === 'pro' ? 'pro' : 'free');
    } catch (err) {
      console.error('fetchTier error:', err);
      const fallback = artist?.tier || 'free';
      setTierSlug(['master','premium'].includes(fallback) ? 'premium' : fallback === 'pro' ? 'pro' : 'free');
    }
    setLoading(false);
  };

  // Both counts below drive the tier limits (canUpload, uploadsRemaining,
  // downloadSalesRemaining). They read only `count`, so a failed request
  // arrived as null and `count || 0` recorded it as "this artist has zero
  // tracks" — which reads as a full, empty quota. These were the two requests
  // returning 503 in the network log, and the reason nobody saw them.
  //
  // Left failing open on purpose: blocking uploads on a transient count error
  // would be worse than briefly over-granting. But it now says so in the
  // console, and a failure no longer overwrites a count we already had.
  const fetchTrackCount = async (artistId) => {
    if (!artistId) return;
    const { count, error } = await supabase
      .from('tracks')
      .select('*', { count: 'exact', head: true })
      .eq('artist_id', artistId);
    if (error) {
      console.warn('[useTier] track count failed, upload limits may not be enforced:',
        error.code, error.message, error.hint || '');
      return; // keep the last known count rather than resetting it to zero
    }
    setTrackCount(count || 0);
  };

  const fetchMonthlyDownloadSalesCount = async (artistId) => {
    if (!artistId) return;
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    const { count, error } = await supabase
      .from('tracks')
      .select('*', { count: 'exact', head: true })
      .eq('artist_id', artistId)
      .gt('download_price', 0)
      .gte('created_at', startOfMonth.toISOString());
    if (error) {
      console.warn('[useTier] monthly download sales count failed, sales limits may not be enforced:',
        error.code, error.message, error.hint || '');
      return;
    }
    setMonthlyDownloadSalesCount(count || 0);
  };

  // Get access rules for current tier
  const access = TIER_ACCESS[tierSlug] || TIER_ACCESS.free;

  // Check if a specific feature is available
  const hasFeature = useCallback((feature) => {
    const rules = TIER_ACCESS[tierSlug] || TIER_ACCESS.free;
    const val = rules[feature];
    if (typeof val === 'boolean') return val;
    if (typeof val === 'number') return val > 0;
    return false;
  }, [tierSlug]);

  // Check if user can upload more tracks
  const canUpload = useCallback(() => {
    if (access.max_singles === Infinity) return true;
    return trackCount < access.max_singles;
  }, [access, trackCount]);

  // Get remaining upload slots
  const uploadsRemaining = useCallback(() => {
    if (access.max_singles === Infinity) return Infinity;
    return Math.max(access.max_singles - trackCount, 0);
  }, [access, trackCount]);

  // Get the minimum tier needed for a feature
  const getMinTier = useCallback((feature) => {
    return FEATURE_LABELS[feature]?.minTier || 'pro';
  }, []);

  // Get feature label info
  const getFeatureInfo = useCallback((feature) => {
    return FEATURE_LABELS[feature] || { name: feature, description: '', minTier: 'pro' };
  }, []);

  // Check tier level (for comparisons)
  const tierLevel = tierSlug === 'premium' ? 3 : tierSlug === 'pro' ? 2 : 1;

  const isPro = tierSlug === 'pro' || tierSlug === 'premium';
  const isPremium = tierSlug === 'premium';
  const isFree = tierSlug === 'free';
  const beatLicences      = access.beat_licences || ['free', 'basic'];
  const maxBeats          = access.max_beats ?? 3;
  const canUploadStems    = access.stems_upload ?? false;
  const canUseBeatAnalytics = access.beat_analytics ?? false;
  const canUseExclusive   = access.exclusive_licence ?? false;

  const downloadSalesLimit = access.download_sales_monthly_limit ?? 0;
  const canAddDownloadSale = isPremium
    ? true
    : (access.download_sales && monthlyDownloadSalesCount < downloadSalesLimit);
  const downloadSalesRemaining = isPremium
    ? Infinity
    : Math.max(0, downloadSalesLimit - monthlyDownloadSalesCount);

  const isListenerPro = listenerTierSlug === 'pro' || listenerTierSlug === 'premium' || listenerTierSlug === 'fan_pro';

  // Publish what the playback gate needs.
  //
  // PlayerProvider wraps TierProvider in AppRouter, so PlayerContext cannot
  // read this context — it is the outer provider. Reordering them to fix that
  // would risk far more than it buys, so the three facts the gate needs are
  // pushed into a module it can read synchronously instead. See
  // utils/trackAccess.js.
  //
  // Fan Pro early access to pre-orders is a paid feature, so getting this
  // wrong in the "off" direction would take a benefit away from paying
  // listeners, and in the "on" direction would leak unreleased music. Both
  // matter, which is why it is derived here from the same value the rest of
  // the app gates on rather than recomputed anywhere.
  useEffect(() => {
    setViewerEntitlement({
      isListenerPro,
      artistId: artist?.id || null,
      isAdmin:  !!isAdmin,
    });
  }, [isListenerPro, artist?.id, isAdmin]);

  return {
    tierSlug,
    listenerTierSlug,
    isListenerPro,
    tierData,
    tierLevel,
    access,
    tierLoading: loading,
    trackCount,
    isPro,
    isPremium,
    isFree,
    hasFeature,
    canUpload,
    uploadsRemaining,
    getMinTier,
    getFeatureInfo,
    refreshTier: () => {
      if (artist?.id) {
        fetchTier(artist.id);
        fetchMonthlyDownloadSalesCount(artist.id);
      }
    },
    refreshListenerTier: () => {
      if (user?.id && !artist) fetchListenerTier(user.id);
    },
    // Download sales limit enforcement
    downloadSalesLimit,
    downloadSalesUsed: monthlyDownloadSalesCount,
    downloadSalesRemaining,
    canAddDownloadSale,
    // Beat maker features
    beatLicences,
    maxBeats,
    canUploadStems,
    canUseBeatAnalytics,
    canUseExclusive,
  };
}

export function useTier() {
  const ctx = useContext(TierContext);
  // If context not available, return safe free-tier defaults
  // This prevents crashes if something renders outside TierProvider
  if (!ctx) {
    return {
      tierSlug: 'free', tierLevel: 1, tierLoading: false, isPro: false,
      isPremium: false, isFree: true, beatLicences: ['free','basic'],
      canUploadStems: false, canUseBeatAnalytics: false, canUseExclusive: false,
      maxBeats: 3, canAddDownloadSale: false, downloadSalesRemaining: 0,
      downloadSalesLimit: 0, downloadSalesUsed: 0, access: {},
      hasFeature: () => false, canUpload: true, uploadsRemaining: 999,
      getMinTier: () => 'free', getFeatureInfo: () => ({}),
      refreshTier: () => {},
    };
  }
  return ctx;
}

export { TIER_ACCESS, FEATURE_LABELS };